import { Box, Dialog, DialogContent, Typography, IconButton } from '@mui/material';
import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DosenInput from '../../../../../pages/new-designs/components/DosenInput';
import DosenButton from '../../../../ui/Buttons/DosenButton';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (title: string, url: string) => Promise<void>;
}

export default function AddURLModal({ open, onClose, onCreate }: Props) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidUrl = (u: string) => {
    try {
      new URL(u);
      return true;
    } catch {
      return false;
    }
  };

  const handleAdd = async () => {
    if (!title.trim() || !isValidUrl(url.trim())) {
      setError('Please provide a valid title and URL.');
      return;
    }

    setLoading(true);
    try {
      await onCreate(title.trim(), url.trim());
      setTitle('');
      setUrl('');
      setError(null);
      onClose();
    } catch (err) {
      setError('Failed to save URL. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          borderRadius: 2,
          border: '1px solid #444444',
          overflow: 'hidden',
          maxWidth: 550
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>
          Add a URL to Knowledge
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#FDFDFD' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          bgcolor: '#0F0F0F',
          px: 3,
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }}
      >
        <DosenInput
          label="Title"
          fullWidth
          value={title}
          onChange={(e: any) => setTitle(e.target.value)}
        />
        <DosenInput
          label="URL"
          fullWidth
          placeholder="https://..."
          value={url}
          onChange={(e: any) => setUrl(e.target.value)}
        />
        {error && <Typography sx={{ color: 'error.main', mt: 1 }}>{error}</Typography>}
        <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '18px' }}>
          Share a link to a webpage or online resource that Elliott should use.
        </Typography>

        <Box display="flex" justifyContent="center">
          <DosenButton
            onClick={handleAdd}
            disabled={!title.trim() || !url.trim()}
            loading={loading}
            loadingText="Adding..."
            sx={{ minWidth: 200 }}
          >
            Add URL
          </DosenButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
